import { useState, useEffect, useMemo } from 'react'
import Header from '@/components/Header'
import Footer from '@/components/Footer'

interface ShipmentRecord {
    rowKey: string
    requested_by: string
    recipient_name: string
    recipient_email: string
    product_id: number
    delivery_type: number
    status: string
    timestamp: string
    orderId: string
}

type SortKey = 'timestamp' | 'requested_by' | 'recipient_name' | 'status'
type SortDir = 'asc' | 'desc'

const PRODUCT_NAMES: Record<number, string> = {
    1: 'YubiKey 5 NFC',
    2: 'YubiKey 5 Nano',
    3: 'YubiKey 5C',
    4: 'YubiKey 5C Nano',
    5: 'YubiKey 5Ci',
    29: 'YubiKey 5C NFC',
}

function StatusBadge({ status }: { status: string }) {
    if (status === 'approved') {
        return <span className="inline-block px-2 py-0.5 rounded-full text-xs font-bold bg-green-100 text-green-700">Approved</span>
    }
    if (status === 'rejected') {
        return <span className="inline-block px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700">Rejected</span>
    }
    return <span className="inline-block px-2 py-0.5 rounded-full text-xs font-bold bg-yellow-100 text-yellow-700">Pending</span>
}

function SortIcon({ col, sortKey, sortDir }: { col: SortKey; sortKey: SortKey; sortDir: SortDir }) {
    if (col !== sortKey) {
        return <span className="ml-1 opacity-40">↕</span>
    }
    return <span className="ml-1">{sortDir === 'asc' ? '↑' : '↓'}</span>
}

function exportCSV(rows: ShipmentRecord[]) {
    const headers = ['Date', 'Requested By', 'Recipient', 'Email', 'Product', 'Delivery', 'Status', 'Order ID']
    const lines = rows.map(s => [
        new Date(s.timestamp).toLocaleDateString(),
        s.requested_by || '',
        s.recipient_name,
        s.recipient_email,
        PRODUCT_NAMES[s.product_id] || `Product ${s.product_id}`,
        s.delivery_type === 2 ? 'Expedited' : 'Standard',
        s.status,
        s.orderId || ''
    ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))

    const csv = [headers.join(','), ...lines].join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `shipment-report-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
}

function ReportPage() {
    const [shipments, setShipments] = useState<ShipmentRecord[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [search, setSearch] = useState('')
    const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all')
    const [sortKey, setSortKey] = useState<SortKey>('timestamp')
    const [sortDir, setSortDir] = useState<SortDir>('desc')
    const [deletingKey, setDeletingKey] = useState<string | null>(null)
    const [confirmKey, setConfirmKey] = useState<string | null>(null)

    useEffect(() => {
        fetch('/api/report')
            .then(r => {
                if (!r.ok) throw new Error('Failed to load')
                return r.json()
            })
            .then((data: ShipmentRecord[]) => {
                setShipments(data)
                setLoading(false)
            })
            .catch(() => {
                setError('Failed to load report data.')
                setLoading(false)
            })
    }, [])

    const filtered = useMemo(() => {
        const q = search.toLowerCase()
        return shipments
            .filter(s => statusFilter === 'all' || s.status === statusFilter)
            .filter(s =>
                !q ||
                s.requested_by?.toLowerCase().includes(q) ||
                s.recipient_name?.toLowerCase().includes(q) ||
                s.recipient_email?.toLowerCase().includes(q) ||
                s.orderId?.toLowerCase().includes(q)
            )
            .sort((a, b) => {
                let av: string | number = ''
                let bv: string | number = ''
                if (sortKey === 'timestamp') { av = a.timestamp; bv = b.timestamp }
                if (sortKey === 'requested_by') { av = a.requested_by || ''; bv = b.requested_by || '' }
                if (sortKey === 'recipient_name') { av = a.recipient_name || ''; bv = b.recipient_name || '' }
                if (sortKey === 'status') { av = a.status; bv = b.status }
                if (av < bv) return sortDir === 'asc' ? -1 : 1
                if (av > bv) return sortDir === 'asc' ? 1 : -1
                return 0
            })
    }, [shipments, search, statusFilter, sortKey, sortDir])

    function handleSort(col: SortKey) {
        if (col === sortKey) {
            setSortDir(d => d === 'asc' ? 'desc' : 'asc')
        } else {
            setSortKey(col)
            setSortDir('asc')
        }
    }

    async function handleDelete(rowKey: string) {
        setDeletingKey(rowKey)
        try {
            const res = await fetch(`/api/report/${rowKey}`, { method: 'DELETE' })
            if (!res.ok) throw new Error('Failed to delete')
            setShipments(prev => prev.filter(s => s.rowKey !== rowKey))
        } catch {
            setError('Failed to delete record. Please try again.')
        } finally {
            setDeletingKey(null)
            setConfirmKey(null)
        }
    }

    const thClass = "text-left px-4 py-3 font-bold whitespace-nowrap cursor-pointer select-none hover:bg-yubico-green-500 transition-colors"

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col">
            <Header variant="default" />

            <main className="flex-grow p-6 py-12">
                <div className="max-w-7xl mx-auto">

                    {/* Header */}
                    <div className="mb-6">
                        <div className="inline-block p-3 bg-yubico-green-50 rounded-2xl mb-4">
                            <i className="iconoir-stats-report text-yubico-green-400 text-3xl"></i>
                        </div>
                        <h2 className="text-3xl font-bold text-gray-900">Shipment Report</h2>
                        <p className="text-gray-500 mt-1">YubiKey shipment requests logged in Azure Table Storage</p>
                    </div>

                    {/* Filter / search bar */}
                    {!loading && !error && (
                        <div className="flex flex-col sm:flex-row gap-3 mb-4">
                            <input
                                type="text"
                                placeholder="Search requester, recipient, email, order ID..."
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-yubico-green-400 outline-none bg-white text-sm"
                            />
                            <select
                                value={statusFilter}
                                onChange={e => setStatusFilter(e.target.value as typeof statusFilter)}
                                className="px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-yubico-green-400 outline-none bg-white text-sm font-medium"
                            >
                                <option value="all">All statuses</option>
                                <option value="pending">Pending</option>
                                <option value="approved">Approved</option>
                                <option value="rejected">Rejected</option>
                            </select>
                            <button
                                onClick={() => exportCSV(filtered)}
                                disabled={filtered.length === 0}
                                className="flex items-center gap-2 px-5 py-2.5 bg-yubico-green-400 hover:bg-yubico-green-500 disabled:bg-gray-200 disabled:text-gray-400 text-white font-bold rounded-xl transition-all text-sm whitespace-nowrap"
                            >
                                <i className="iconoir-download text-base"></i>
                                Export CSV
                            </button>
                        </div>
                    )}

                    {/* Row count */}
                    {!loading && !error && (
                        <p className="text-xs text-gray-400 mb-2">
                            {filtered.length} of {shipments.length} {shipments.length === 1 ? 'record' : 'records'}
                        </p>
                    )}

                    {loading && (
                        <div className="flex items-center justify-center py-20">
                            <span className="w-8 h-8 border-4 border-yubico-green-400 border-t-transparent rounded-full animate-spin" />
                        </div>
                    )}

                    {error && (
                        <div className="p-4 bg-red-50 text-red-700 rounded-xl border border-red-100">{error}</div>
                    )}

                    {!loading && !error && (
                        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="bg-yubico-green-400 text-white">
                                            <th className={thClass} onClick={() => handleSort('timestamp')}>
                                                Date <SortIcon col="timestamp" sortKey={sortKey} sortDir={sortDir} />
                                            </th>
                                            <th className={thClass} onClick={() => handleSort('requested_by')}>
                                                Requested By <SortIcon col="requested_by" sortKey={sortKey} sortDir={sortDir} />
                                            </th>
                                            <th className={thClass} onClick={() => handleSort('recipient_name')}>
                                                Recipient <SortIcon col="recipient_name" sortKey={sortKey} sortDir={sortDir} />
                                            </th>
                                            <th className="text-left px-4 py-3 font-bold whitespace-nowrap">Email</th>
                                            <th className="text-left px-4 py-3 font-bold whitespace-nowrap">Product</th>
                                            <th className="text-left px-4 py-3 font-bold whitespace-nowrap">Delivery</th>
                                            <th className={thClass} onClick={() => handleSort('status')}>
                                                Status <SortIcon col="status" sortKey={sortKey} sortDir={sortDir} />
                                            </th>
                                            <th className="text-left px-4 py-3 font-bold whitespace-nowrap">Order ID</th>
                                            <th className="px-4 py-3"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filtered.length === 0 ? (
                                            <tr>
                                                <td colSpan={9} className="text-center py-12 text-gray-400">
                                                    No records match your filters.
                                                </td>
                                            </tr>
                                        ) : (
                                            filtered.map(s => (
                                                <tr key={s.rowKey} className="border-t border-gray-100 hover:bg-gray-50 transition-colors">
                                                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                                                        {new Date(s.timestamp).toLocaleDateString()}
                                                    </td>
                                                    <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">
                                                        {s.requested_by || '—'}
                                                    </td>
                                                    <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                                                        {s.recipient_name}
                                                    </td>
                                                    <td className="px-4 py-3 text-gray-500">
                                                        {s.recipient_email}
                                                    </td>
                                                    <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                                                        {PRODUCT_NAMES[s.product_id] || `Product ${s.product_id}`}
                                                    </td>
                                                    <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                                                        {s.delivery_type === 2 ? 'Expedited' : 'Standard'}
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <StatusBadge status={s.status} />
                                                    </td>
                                                    <td className="px-4 py-3 text-gray-500 font-mono text-xs whitespace-nowrap">
                                                        {s.orderId || '—'}
                                                    </td>
                                                    <td className="px-4 py-3 whitespace-nowrap text-right">
                                                        {confirmKey === s.rowKey ? (
                                                            <div className="flex items-center gap-2 justify-end">
                                                                <span className="text-xs text-gray-500">Delete?</span>
                                                                <button
                                                                    onClick={() => handleDelete(s.rowKey)}
                                                                    disabled={deletingKey === s.rowKey}
                                                                    className="px-2 py-1 text-xs font-bold bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors disabled:opacity-50"
                                                                >
                                                                    {deletingKey === s.rowKey ? '...' : 'Yes'}
                                                                </button>
                                                                <button
                                                                    onClick={() => setConfirmKey(null)}
                                                                    className="px-2 py-1 text-xs font-bold bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition-colors"
                                                                >
                                                                    No
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <button
                                                                onClick={() => setConfirmKey(s.rowKey)}
                                                                className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                                                title="Delete record"
                                                            >
                                                                <i className="iconoir-trash text-base"></i>
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            </main>

            <Footer variant="default" />
        </div>
    )
}

export default ReportPage
